import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { schema } from "./validations";
import {
  DESCRIPTION,
  UPDATED_BY,
  ACTIVE,
  DOCUMENT_TYPE_ID,
  TRANSACTIONAL_PROCESSOR,
  DOCUMENT_TYPE,
  UPDATED_AT,
  COMMUNITY_ID,
} from "./consts";
import {
  getRequiredFields,
  removeEmptyFields,
  setEmptyFieldsToNull,
} from "@/lib/utils";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import {
  useAddDocumentTypeMutation,
  useGetDocumentTypeById,
  useUpdateDocumentTypeMutation,
} from "@/services";
import { useEffect, useState } from "react";
import { toast } from "@/components/Toast";
import { useQueryClient } from "@tanstack/react-query";
import moment from "moment";

const useAddEditConfig = () => {
  const { id, documentTypeId, action } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const isAdd = !documentTypeId;
  const isView = documentTypeId && action == "view";
  const [keys, setKeys] = useState({
    documentType: Math.random() * 3,
    processor: Math.random() * 3,
  });

  const form = useForm<any>({
    defaultValues: {
      [DOCUMENT_TYPE_ID]: "",
      [DOCUMENT_TYPE]: "",
      [TRANSACTIONAL_PROCESSOR]: "",
      [ACTIVE]: true,
      [DESCRIPTION]: "",
      [UPDATED_AT]: "",
      [UPDATED_BY]: "",
      [COMMUNITY_ID]: id,
    },
    resolver: zodResolver(schema),
  });

  const requiredFields = getRequiredFields(schema);

  const communityDetail = useSelector(
    (state: RootState) => state.currentData.communityData
  );

  const isMasterData = location?.pathname?.toLowerCase()?.includes("master-data");
  const communityId = isMasterData ? communityDetail?.communityId : id;
  const documentTypeListPath = isMasterData
    ? `/${routes.cam}/${routes.masterData}/${routes.documentTypeConfig}`
    : `/${routes.cam}/${routes.communities}/${id}/${routes.documentTypeConfig}`;

  const breadcrumbData = [
    ...(isMasterData
      ? [
          {
            link: `/${routes.cam}/${routes.masterData}`,
            name: "Master Data",
          },
          {
            link: "",
            name: <span>{"Document Type Config"}</span>,
          },
        ]
      : [
          {
            link: `/${routes.cam}/${routes.communities}`,
            name: `Community - ${(communityDetail as any)?.name || ""}`,
          },
        ]),
    {
      link: documentTypeListPath,
      name: <span>{"Document Types"}</span>,
    },
    {
      link: "",
      name: (
        <span className="text-primary">
          {isAdd
            ? "Add Document Types"
            : isView
            ? "View Document Type"
            : "Edit Document Type"}
        </span>
      ),
    },
  ];

  const { mutate: addInfo, isPending: isAddPending } =
    useAddDocumentTypeMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["document-type", communityId],
        });
        onBackClick();
        toast.success("Document Type Added Successfully");
      },
    });

  const { mutate: updateInfo, isPending: isUpdatePending } =
    useUpdateDocumentTypeMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["document-type", communityId],
        });
        onBackClick();
        toast.success("Document Type Updated Successfully");
      },
    });

  const { data: docTypeDetails, isLoading: isDetailLoading } =
    useGetDocumentTypeById({
      id: documentTypeId as string,
      queryParams: {
        enabled: !!documentTypeId,
      },
    });

  useEffect(() => {
    if (docTypeDetails) {
      form.reset({
        [DOCUMENT_TYPE_ID]:
          docTypeDetails?.[DOCUMENT_TYPE_ID]?.toString() || "",
        [DOCUMENT_TYPE]: docTypeDetails?.[DOCUMENT_TYPE] || "",
        [TRANSACTIONAL_PROCESSOR]:
          docTypeDetails?.[TRANSACTIONAL_PROCESSOR] || "",
        [ACTIVE]: docTypeDetails?.[ACTIVE] || true,
        [DESCRIPTION]: docTypeDetails?.[DESCRIPTION] || "",
        [UPDATED_AT]: docTypeDetails?.[UPDATED_AT]
          ? moment(docTypeDetails?.[UPDATED_AT]).format("MM/DD/YYYY")
          : "",
        [UPDATED_BY]: docTypeDetails?.updatedByUserName || docTypeDetails?.[UPDATED_BY] || "",
        [COMMUNITY_ID]: docTypeDetails?.[COMMUNITY_ID]?.toString() || "",
      });
    }
    setKeys({
      documentType: Math.random() * 2,
      processor: Math.random() * 2,
    });
  }, [docTypeDetails]);

  const onSubmit = (data: any) => {
    let _data = removeEmptyFields({
      ...data,
    });
    _data = setEmptyFieldsToNull(_data);

    if (documentTypeId) {
      updateInfo({
        id: documentTypeId,
        data: {
          ..._data,
          [COMMUNITY_ID]: communityId,
          [DOCUMENT_TYPE]: (_data[DOCUMENT_TYPE] as any)?.toUpperCase(),
          [TRANSACTIONAL_PROCESSOR]: (
            _data[TRANSACTIONAL_PROCESSOR] as any
          )?.toUpperCase(),
        },
      });
    } else {
      addInfo({
        data: {
          ..._data,
          [COMMUNITY_ID]: communityId,
          [DOCUMENT_TYPE]: (_data[DOCUMENT_TYPE] as any)?.toUpperCase(),
          [TRANSACTIONAL_PROCESSOR]: (
            _data[TRANSACTIONAL_PROCESSOR] as any
          )?.toUpperCase(),
        },
      });
    }
  };

  const onBackClick = () => {
    navigate(documentTypeListPath);
  };

  return {
    form,
    onSubmit,
    isAdd,
    isDetailLoading,
    breadcrumbData,
    isView,
    onBackClick,
    requiredFields,
    onCancel: onBackClick,
    isAddPending,
    isUpdatePending,
    documentTypeId,
    keys,
  };
};

export default useAddEditConfig;
